/* eslint-disable react-refresh/only-export-components */
import { useCallback, useEffect, useRef, useState } from 'react';
import jsQR from 'jsqr';
import { useFlowDisplayLanguage } from '../displayLanguage';
import type { QrScanResult } from '../types';

type QrScannerDialogProps = {
  open: boolean;
  remoteStream: MediaStream | null;
  preferLocalCamera?: boolean;
  onLocalFallback?: () => void;
  onResult: (result: QrScanResult) => void;
};

type ScanSource = 'waiting' | 'remote' | 'local';

const REMOTE_STREAM_WAIT_MS = 5000;
const REMOTE_FRAME_READY_MS = 3000;
export const QR_SCAN_TIMEOUT_MS = 30000;

export const hasLiveVideoTrack = (stream: MediaStream | null): stream is MediaStream =>
  !!stream && stream.getVideoTracks().some((track) => track.readyState === 'live' && !track.muted);

export const shouldAdoptRemoteStream = (
  currentSource: ScanSource,
  localCameraStarted: boolean,
  attachedStream: MediaStream | null,
  nextStream: MediaStream | null,
) => (
  !localCameraStarted
  && hasLiveVideoTrack(nextStream)
  && (currentSource === 'waiting' || (currentSource === 'remote' && attachedStream !== nextStream))
);

export class LocalCameraAttemptGate {
  private started = false;

  claim() {
    if (this.started) return false;
    this.started = true;
    return true;
  }

  hasStarted() { return this.started; }
  reset() { this.started = false; }
}

export class RemotePreviewHealthMonitor {
  private timer: ReturnType<typeof setTimeout> | null = null;
  private tracks: MediaStreamTrack[] = [];
  private onUnavailable: EventListener | null = null;

  watch(stream: MediaStream, onUnavailable: () => void, timeoutMs = REMOTE_FRAME_READY_MS) {
    this.clear();
    this.tracks = stream.getVideoTracks();
    this.onUnavailable = onUnavailable;
    this.tracks.forEach((track) => {
      track.addEventListener('ended', onUnavailable);
      track.addEventListener('mute', onUnavailable);
    });
    this.timer = setTimeout(onUnavailable, timeoutMs);
  }

  markFrameReady() {
    if (this.timer !== null) clearTimeout(this.timer);
    this.timer = null;
  }

  clear() {
    this.markFrameReady();
    if (this.onUnavailable) {
      this.tracks.forEach((track) => {
        track.removeEventListener('ended', this.onUnavailable!);
        track.removeEventListener('mute', this.onUnavailable!);
      });
    }
    this.tracks = [];
    this.onUnavailable = null;
  }
}

export function QrScannerDialog({
  open,
  remoteStream,
  preferLocalCamera = false,
  onLocalFallback,
  onResult,
}: QrScannerDialogProps) {
  const displayLanguage = useFlowDisplayLanguage();
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const frameRequestRef = useRef<number | null>(null);
  const remoteWaitTimerRef = useRef<number | null>(null);
  const remoteFallbackDeadlineRef = useRef<number | null>(null);
  const remoteHealthMonitorRef = useRef(new RemotePreviewHealthMonitor());
  const scanTimeoutRef = useRef<number | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const remoteStreamRef = useRef(remoteStream);
  remoteStreamRef.current = remoteStream;
  const scanSessionRef = useRef(0);
  const attachmentRef = useRef(0);
  const currentSourceRef = useRef<ScanSource>('waiting');
  const localCameraGateRef = useRef(new LocalCameraAttemptGate());
  const fallbackToLocalRef = useRef<() => void>(() => undefined);
  const settledRef = useRef(false);
  const onResultRef = useRef(onResult);
  const onLocalFallbackRef = useRef(onLocalFallback);
  const [scanSource, setScanSource] = useState<ScanSource>('waiting');

  useEffect(() => {
    onResultRef.current = onResult;
  }, [onResult]);

  useEffect(() => {
    onLocalFallbackRef.current = onLocalFallback;
  }, [onLocalFallback]);

  const stopLocalStream = useCallback(() => {
    if (!localStreamRef.current) {
      return;
    }
    localStreamRef.current.getTracks().forEach((track) => track.stop());
    localStreamRef.current = null;
  }, []);

  const clearFrameLoop = useCallback(() => {
    if (frameRequestRef.current !== null) {
      cancelAnimationFrame(frameRequestRef.current);
      frameRequestRef.current = null;
    }
  }, []);

  const clearPendingStart = useCallback(() => {
    if (remoteWaitTimerRef.current !== null) {
      window.clearTimeout(remoteWaitTimerRef.current);
      remoteWaitTimerRef.current = null;
    }
  }, []);

  const clearRemoteFrameWait = useCallback(() => {
    remoteHealthMonitorRef.current.markFrameReady();
  }, []);

  const clearRemoteMonitoring = useCallback(() => {
    remoteHealthMonitorRef.current.clear();
  }, []);

  const getRemoteFallbackRemainingMs = useCallback(() => {
    const deadline = remoteFallbackDeadlineRef.current;
    return deadline === null ? 0 : Math.max(0, deadline - Date.now());
  }, []);

  const clearScanTimeout = useCallback(() => {
    if (scanTimeoutRef.current !== null) {
      window.clearTimeout(scanTimeoutRef.current);
      scanTimeoutRef.current = null;
    }
  }, []);

  const cleanup = useCallback(() => {
    scanSessionRef.current += 1;
    attachmentRef.current += 1;
    clearFrameLoop();
    clearPendingStart();
    clearRemoteMonitoring();
    clearScanTimeout();
    stopLocalStream();
    remoteFallbackDeadlineRef.current = null;
    localCameraGateRef.current.reset();
    currentSourceRef.current = 'waiting';
    setScanSource('waiting');

    const video = videoRef.current;
    if (video) {
      video.pause();
      video.srcObject = null;
    }
  }, [clearFrameLoop, clearPendingStart, clearRemoteMonitoring, clearScanTimeout, stopLocalStream]);

  const finishScan = useCallback((result: QrScanResult) => {
    if (settledRef.current) {
      return;
    }
    settledRef.current = true;
    cleanup();
    onResultRef.current(result);
  }, [cleanup]);

  const scanFrame = useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || settledRef.current) {
      return;
    }

    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) {
      finishScan({
        status: 'error',
        message: displayLanguage === 'en' ? 'Scanner initialization failed' : 'スキャナーを初期化できませんでした',
      });
      return;
    }

    if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA && video.videoWidth > 0 && video.videoHeight > 0) {
      if (currentSourceRef.current === 'remote') clearRemoteFrameWait();
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const code = jsQR(imageData.data, imageData.width, imageData.height);
      const value = code?.data.trim();
      if (value) {
        finishScan({ status: 'success', value });
        return;
      }
    }

    frameRequestRef.current = requestAnimationFrame(scanFrame);
  }, [clearRemoteFrameWait, displayLanguage, finishScan]);

  const attachStream = useCallback((stream: MediaStream, source: Exclude<ScanSource, 'waiting'>) => {
    if (!hasLiveVideoTrack(stream)) {
      if (source === 'remote') fallbackToLocalRef.current();
      else finishScan({
        status: 'error',
        message: displayLanguage === 'en' ? 'Unable to access any camera' : '利用できるカメラが見つかりません',
      });
      return;
    }
    const video = videoRef.current;
    if (!video) {
      if (source === 'remote') fallbackToLocalRef.current();
      else finishScan({
        status: 'error',
        message: displayLanguage === 'en' ? 'Video element is unavailable' : '映像表示を初期化できませんでした',
      });
      return;
    }

    clearFrameLoop();
    clearPendingStart();
    clearRemoteMonitoring();
    const attachment = attachmentRef.current + 1;
    attachmentRef.current = attachment;
    currentSourceRef.current = source;
    setScanSource(source);
    video.srcObject = stream;
    if (source === 'remote') {
      const onUnavailable = () => fallbackToLocalRef.current();
      remoteHealthMonitorRef.current.watch(
        stream,
        onUnavailable,
        Math.min(REMOTE_FRAME_READY_MS, getRemoteFallbackRemainingMs()),
      );
    }
    void video.play()
      .then(() => {
        if (settledRef.current || attachmentRef.current !== attachment || currentSourceRef.current !== source) return;
        scanFrame();
      })
      .catch(() => {
        if (settledRef.current || attachmentRef.current !== attachment) return;
        if (source === 'remote') fallbackToLocalRef.current();
        else finishScan({
          status: 'error',
          message: displayLanguage === 'en' ? 'Unable to start camera preview' : 'カメラ映像を開始できませんでした',
        });
      });
  }, [
    clearFrameLoop,
    clearPendingStart,
    clearRemoteMonitoring,
    displayLanguage,
    finishScan,
    getRemoteFallbackRemainingMs,
    scanFrame,
  ]);

  const startLocalScan = useCallback(async () => {
    if (settledRef.current || !localCameraGateRef.current.claim()) {
      return;
    }
    onLocalFallbackRef.current?.();
    const scanSession = scanSessionRef.current;
    if (!navigator.mediaDevices?.getUserMedia) {
      finishScan({
        status: 'error',
        message: displayLanguage === 'en' ? 'This browser does not support camera access' : 'このブラウザではカメラを利用できません',
      });
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' } },
        audio: false,
      });
      if (settledRef.current || scanSessionRef.current !== scanSession) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      localStreamRef.current = stream;
      attachStream(stream, 'local');
    } catch {
      finishScan({
        status: 'error',
        message: displayLanguage === 'en' ? 'Unable to access any camera' : '利用できるカメラが見つかりません',
      });
    }
  }, [attachStream, displayLanguage, finishScan]);

  const fallbackToLocal = useCallback(() => {
    if (settledRef.current || localCameraGateRef.current.hasStarted()) return;
    clearFrameLoop();
    clearPendingStart();
    clearRemoteMonitoring();
    const video = videoRef.current;
    if (video) {
      video.pause();
      video.srcObject = null;
    }
    currentSourceRef.current = 'waiting';
    setScanSource('waiting');
    const remainingMs = getRemoteFallbackRemainingMs();
    remoteWaitTimerRef.current = window.setTimeout(() => {
      remoteWaitTimerRef.current = null;
      void startLocalScan();
    }, remainingMs);
  }, [
    clearFrameLoop,
    clearPendingStart,
    clearRemoteMonitoring,
    getRemoteFallbackRemainingMs,
    startLocalScan,
  ]);

  useEffect(() => {
    fallbackToLocalRef.current = fallbackToLocal;
  }, [fallbackToLocal]);

  useEffect(() => {
    if (!open) {
      settledRef.current = true;
      cleanup();
      return;
    }

    settledRef.current = false;
    scanSessionRef.current += 1;
    remoteFallbackDeadlineRef.current = Date.now() + (preferLocalCamera ? 0 : REMOTE_STREAM_WAIT_MS);
    currentSourceRef.current = 'waiting';
    setScanSource('waiting');
    scanTimeoutRef.current = window.setTimeout(() => {
      finishScan({ status: 'timeout' });
    }, QR_SCAN_TIMEOUT_MS);

    const initialRemoteStream = remoteStreamRef.current;
    if (preferLocalCamera) {
      void startLocalScan();
    } else if (hasLiveVideoTrack(initialRemoteStream)) {
      attachStream(initialRemoteStream, 'remote');
    } else {
      remoteWaitTimerRef.current = window.setTimeout(() => {
        void startLocalScan();
      }, getRemoteFallbackRemainingMs());
    }

    return () => {
      settledRef.current = true;
      cleanup();
    };
  }, [attachStream, cleanup, finishScan, getRemoteFallbackRemainingMs, open, preferLocalCamera, startLocalScan]);

  useEffect(() => {
    if (!open || preferLocalCamera || !remoteStream || localCameraGateRef.current.hasStarted()) return;
    const tryAttachRemote = () => {
      const attachedStream = videoRef.current?.srcObject instanceof MediaStream
        ? videoRef.current.srcObject
        : null;
      if (shouldAdoptRemoteStream(
        currentSourceRef.current,
        localCameraGateRef.current.hasStarted(),
        attachedStream,
        remoteStream,
      )) attachStream(remoteStream, 'remote');
    };
    const tracks = remoteStream.getVideoTracks();
    tracks.forEach((track) => track.addEventListener('unmute', tryAttachRemote));
    remoteStream.addEventListener('addtrack', tryAttachRemote);
    tryAttachRemote();
    return () => {
      tracks.forEach((track) => track.removeEventListener('unmute', tryAttachRemote));
      remoteStream.removeEventListener('addtrack', tryAttachRemote);
    };
  }, [attachStream, open, preferLocalCamera, remoteStream]);

  if (!open) {
    return null;
  }

  const title = displayLanguage === 'en' ? 'QR Scan' : 'QR読み取り';
  const description = scanSource === 'remote'
    ? (displayLanguage === 'en' ? 'Scanning with robot camera...' : 'ロボットカメラで読み取り中...')
    : scanSource === 'local'
      ? (displayLanguage === 'en' ? 'Scanning with device camera...' : '端末カメラで読み取り中...')
      : (displayLanguage === 'en' ? 'Preparing camera...' : 'カメラを準備中...');
  const cancelLabel = displayLanguage === 'en' ? 'Cancel' : 'キャンセル';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4 animate-[fade-in_200ms_ease]">
      <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl animate-[pop-in_200ms_ease-out]">
        <div className="border-b border-slate-200 px-5 py-4">
          <h2 className="text-lg font-semibold text-slate-800">{title}</h2>
          <p className="mt-1 text-sm text-slate-600">{description}</p>
        </div>
        <div className="px-5 py-4">
          <div className="relative grid aspect-[4/3] place-items-center overflow-hidden rounded-xl bg-black">
            <video
              ref={videoRef}
              className={`h-full w-full object-cover${scanSource === 'remote' ? ' qr-scanner__video is-rotated' : ''}`}
              playsInline
              autoPlay
              muted
            />
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
              <div className="relative h-40 w-40 rounded-2xl border-4 border-blue-300/80 shadow-[0_0_0_9999px_rgba(15,23,42,0.28)]">
                <span className="qr-scan-line" aria-hidden="true" />
              </div>
            </div>
          </div>
          <canvas ref={canvasRef} className="hidden" />
        </div>
        <div className="flex justify-end border-t border-slate-200 px-5 py-4">
          <button
            type="button"
            onClick={() => finishScan({ status: 'cancelled' })}
            className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50"
          >
            {cancelLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
